import { useState } from 'react';
import {
    createShowcase,
    buildVitrinaUrl,
    buildWhatsAppMessage,
    buildEmailParts,
    openWhatsApp,
    openEmail,
    openSystemShare,
    copyVitrinaUrl,
    copyTextWhenReady,
} from '../lib/vitrina-queries';
import type { ShareAction } from '../components/ui/ShareOptionsDialog';

interface UseVitrinaShareOptions {
    /** Label passed to the native share sheet ('Propiedad' vs 'Propiedades'). */
    shareTitle: string;
    /** Page-specific feedback after a successful clipboard copy. */
    onCopied: () => void;
}

/**
 * Shared "share a vitrina" orchestration for the property detail + list pages.
 * Wraps the lib/vitrina-queries primitives (createShowcase → copy / whatsapp /
 * email / system share) behind one handler so the flow lives in a single place.
 *
 * `getIds` is a function (not a value) so the handler reads the CURRENT id set
 * at click time rather than a stale render snapshot.
 *
 * SAFARI-CRITICAL — do not reorder: `createShowcase()` is a network call. It is
 * invoked synchronously at the top and its promise is handed UNAWAITED to
 * copyTextWhenReady on the copy path. Safari drops the tap's user-activation the
 * instant you await a network call, so awaiting the showcase before the clipboard
 * write makes navigator.clipboard throw NotAllowedError. See copyTextWhenReady
 * in lib/vitrina-queries.ts.
 */
export function useVitrinaShare(
    getIds: () => string[],
    { shareTitle, onCopied }: UseVitrinaShareOptions,
) {
    const [isCreatingShowcase, setIsCreatingShowcase] = useState(false);

    const handleShareAction = async (action: ShareAction, hideContact: boolean, waWindow: Window | null) => {
        if (isCreatingShowcase) return;
        setIsCreatingShowcase(true);
        // Mint the vitrina once, but DON'T await it yet for the copy path: Safari
        // drops the tap's user-activation as soon as we await a network call, so a
        // later navigator.clipboard.writeText() throws NotAllowedError. We hand the
        // clipboard the in-flight promise synchronously instead (copyTextWhenReady).
        const showcase = createShowcase(getIds(), hideContact);
        try {
            if (action === 'copy') {
                await copyTextWhenReady(showcase.then((r) => buildVitrinaUrl(r.short_code)));
                onCopied();
                return;
            }

            const result = await showcase;
            const vitrinaUrl = buildVitrinaUrl(result.short_code);
            const message = buildWhatsAppMessage(
                result.properties_summary,
                result.created_by_name,
                result.agency_name,
                vitrinaUrl,
                hideContact,
                result.created_by_phone,
            );

            if (action === 'whatsapp') {
                openWhatsApp(message, waWindow);
            } else if (action === 'email') {
                const { subject, body } = buildEmailParts(
                    result.properties_summary,
                    result.created_by_name,
                    result.agency_name,
                    vitrinaUrl,
                    hideContact,
                    result.created_by_phone,
                );
                openEmail(subject, body);
            } else if (action === 'system') {
                const ok = await openSystemShare(shareTitle, message, vitrinaUrl);
                if (!ok) {
                    // navigator.share not available — copy as fallback
                    await copyVitrinaUrl(vitrinaUrl);
                    onCopied();
                }
            }
        } catch (err: any) {
            if (waWindow && !waWindow.closed) waWindow.close();
            alert(`Error al compartir: ${err?.message || 'Intenta de nuevo'}`);
        } finally {
            setIsCreatingShowcase(false);
        }
    };

    return { isCreatingShowcase, handleShareAction };
}
